import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { takeUntil } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';

import { BaseComponent } from '../../core';
import {
  AbstractAuthenticationService,
  AbstractStateService,
  DialogService,
  ErrorHandlerServiceService,
  ResendPinCountdownService,
} from '../../services';
import {
  LibCoreLayoutModule,
  SuccessCheckAnimationDialogComponent,
  SuccessCheckAnimationDialogComponentData,
} from '../core-layout';
import { PinCodeInputModule } from '../pin-code-input';

export interface UpdatePasswordPinDialogComponentData {
  username: string;
  password: string;
  passwordRequestId: string;
  isUsernameEmail: boolean;
}

@Component({
  selector: 'lib-update-password-pin-dialog',
  templateUrl: './update-password-pin-dialog.component.html',
  styleUrls: ['./update-password-pin-dialog.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    LibCoreLayoutModule,
    TranslateModule,
    PinCodeInputModule,
    ReactiveFormsModule,
    // material design
    MatButtonModule,
  ],
})
export class UpdatePasswordPinDialogComponent extends BaseComponent {
  public isLoading = false;
  public resendPinCountdownInSecond = 0;
  public isUsernameEmail: boolean;

  public controlPin = new FormControl(null, [Validators.required]);

  public username: string | undefined;
  private password: string | undefined;
  private passwordRequestId: string | undefined;

  constructor(
    @Inject(MAT_DIALOG_DATA) private data: UpdatePasswordPinDialogComponentData,
    private dialogRef: MatDialogRef<UpdatePasswordPinDialogComponent>,
    private dialogService: DialogService,
    private errorHandlerServiceService: ErrorHandlerServiceService,
    private stateService: AbstractStateService,
    private resendPinCountdownService: ResendPinCountdownService,
    private authenticationService: AbstractAuthenticationService
  ) {
    super();

    this.username = this.data?.username;
    this.password = this.data?.password;
    this.passwordRequestId = this.data?.passwordRequestId;
    this.isUsernameEmail = !!this.data?.isUsernameEmail;

    this.resendPinCountdownService.resendPinCountdownInSecond$
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe((v) => (this.resendPinCountdownInSecond = v));
  }

  public isAbleToSubmit(): boolean {
    return !this.isLoading && this.controlPin.valid;
  }

  public async submit() {
    if (!this.isAbleToSubmit()) {
      return;
    }

    try {
      this.isLoading = true;

      const token =
        await this.authenticationService.resolveChangePasswordRequest(
          this.passwordRequestId,
          this.controlPin.value
        );
      if (token?.length) {
        this.stateService.setToken(`Bearer ${token}`);
        this.displaySuccessCheckAnimationDialog();
        this.closeDialog();
      }
    } catch (e) {
      this.errorHandlerServiceService.handleError(e);
    } finally {
      this.isLoading = false;
    }
  }

  public isAbleToResendPin(): boolean {
    return (
      !this.isLoading &&
      !!this.username &&
      !!this.password &&
      this.resendPinCountdownInSecond <= 0
    );
  }

  public async resendPin() {
    if (!this.isAbleToResendPin()) {
      return;
    }

    try {
      this.isLoading = true;

      this.passwordRequestId =
        await this.authenticationService.createChangePasswordRequest(
          this.username,
          this.password
        );
      if (this.passwordRequestId?.length) {
        this.resendPinCountdownService.setResendPinCountdownNow();
      }
    } catch (e) {
      this.errorHandlerServiceService.handleError(e);
    } finally {
      this.isLoading = false;
    }
  }

  public closeDialog(): void {
    this.dialogRef.close(null);
  }

  private displaySuccessCheckAnimationDialog(): void {
    this.dialogService.openDialog(
      SuccessCheckAnimationDialogComponent,
      this.dialogService.createSmallDialogConfig({
        text: 'update-password-pin-dialog.text.password-updated',
      } as SuccessCheckAnimationDialogComponentData)
    );
  }
}
